import { createContext, useContext, useState, useEffect } from 'react';
import { products as initialProducts, categories as defaultCategories, subcategories } from '../../data/data';

const ProductStoreContext = createContext(null);

const DEFAULT_BANNER = "https://res.cloudinary.com/dbiftzkui/image/upload/v1778422033/farmley-assets/homepage-banner-hero.jpg";

function getInitialHomepage() {
  return {
    hero: {
      bg: DEFAULT_BANNER, 
      main: '',
      top: '',
      bot: '',
    },
    promos: {
      red: null,
      yellow: null,
    },
    video: '' 
  };
}

function getInitialSiteContent() {
  return {
    about: {
      title: 'Purest Dry Fruits, From Our Farm to Your Home',
      eyebrow: 'OUR STORY',
      lead: 'KOA Dry Fruits was born from a simple idea: that everyone deserves access to the highest quality, 100% natural nuts and dried fruits without the premium markup. We source directly from the best farms across the globe to ensure every bite is packed with nutrition and flavor.',
    },
    mission: {
      title: 'Making Healthy Eating Easy for Every Household',
      eyebrow: 'Our Mission',
      text1: "To become India's most trusted destination for premium dry fruits, known for our uncompromising quality, transparency, and farm-to-table approach.",
      text2: 'We believe that health is the greatest wealth, and we are committed to helping our customers lead better lives through nature\'s most powerful superfoods.'
    }
  };
}

function getInitialCoupons() {
  return [
    { id: 1, code: 'KOA10', discount: 10, type: 'percent', active: true, minOrder: 500  },
    { id: 2, code: 'SAVE50',    discount: 50, type: 'flat',    active: true, minOrder: 1000 },
    { id: 3, code: 'NUTS20',    discount: 20, type: 'percent', active: false, minOrder: 300 },
  ];
}

function getFullUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('http')) return url;
  if (url.startsWith('//')) return 'https:' + url;
  // If it's a relative path starting with /
  if (url.startsWith('/')) return `https://mediumpurple-squirrel-426661.hostingersite.com${url}`;
  return url;
}

const API_BASE = window.location.hostname === 'localhost' 
  ? 'http://localhost:5000/api' 
  : 'https://mediumpurple-squirrel-426661.hostingersite.com/api';

export function ProductStoreProvider({ children }) {
  const [products,          setProducts]          = useState([]);
  const [blogs,             setBlogs]             = useState([]);
  const [coupons,           setCoupons]           = useState([]);
  const [categories,        setCategories]        = useState([]);
  const [homepage,          setHomepage]          = useState(getInitialHomepage());
  const [settings,          setSettings]          = useState({ logo: null });
  const [siteContent,       setSiteContent]       = useState(getInitialSiteContent());
  const [featuredProductIds,setFeaturedProductIds]= useState([]);
  const [maintenanceMode,   setMaintenanceMode]   = useState(false);
  const [paymentGatewayEnabled, setPaymentGatewayEnabled] = useState(true);
  const [codEnabled, setCodEnabled] = useState(true);
  const [loading,           setLoading]           = useState(true);

  // Helper to fetch and update state with fallback
  const fetchGlobal = async (key, setter, fallback) => {
    try {
      const res = await fetch(`${API_BASE}/global/${key}`);
      if (res.ok) {
        let data = await res.json();
        // If data is an object with a 'value' property (from our new save format)
        if (data && typeof data === 'object' && 'value' in data) data = data.value;
        
        if (data !== null && data !== undefined) setter(data);
        else if (fallback !== undefined) setter(fallback);
      } else if (fallback) {
        setter(fallback);
      }
    } catch (err) {
      console.warn(`⚠️ Failed to fetch ${key}:`, err);
      if (fallback) setter(fallback);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      
      // Load Products and Blogs
      try {
        const [pRes, bRes] = await Promise.all([
          fetch(`${API_BASE}/products`),
          fetch(`${API_BASE}/blogs`)
        ]);
        if (pRes.ok) {
          const dbProducts = await pRes.json();
          const final = [...dbProducts];
          // Merge with initial data for items not in DB yet
          const initial = initialProducts.map(p => ({
            ...p,
            status: 'published',
            stock: 'instock',
            sku: `FLY-${p.id.toString().padStart(4,'0')}`,
            images: [{ url: p.image, featured: true, alt: p.name }],
            shortDesc: p.shortDesc || '',
            variants: [
              { weight: '250g', price: p.price, mrp: p.mrp, stock: 'instock' },
              { weight: '500g', price: p.price * 1.9, mrp: p.mrp * 1.9, stock: 'instock' },
              { weight: '1Kg (Pack of 2)', price: p.price * 3.5, mrp: p.mrp * 3.5, stock: 'instock' }
            ]
          }));
          initial.forEach(mp => {
            const existingIdx = final.findIndex(dp => dp.slug === mp.slug);
            if (existingIdx !== -1) {
              // If it exists in DB, we still want to force sync the new descriptions/highlights if they were updated in data.js
              // especially for the 'almonds' category as requested.
              if (mp.category === 'almonds') {
                final[existingIdx] = {
                  ...final[existingIdx],
                  shortDesc: mp.shortDesc,
                  description: mp.description,
                  highlights: mp.highlights
                };
              }
            } else {
              final.push(mp);
            }
          });
          setProducts(final);
        }

        // Handle Blogs
        if (bRes.ok) {
          const dbBlogs = await bRes.json();
          setBlogs(dbBlogs);
        }
      } catch (err) {
        console.error("Products/Blogs fetch error:", err);
        setProducts(initialProducts.map(p => ({ ...p, status: 'published', stock: 'instock' })));
      }

      // Load other globals in parallel
      await Promise.all([
        fetchGlobal('coupons', setCoupons, getInitialCoupons()),
        fetchGlobal('categories', setCategories, defaultCategories),
        fetchGlobal('homepage', setHomepage, getInitialHomepage()),
        fetchGlobal('settings', setSettings, { logo: null }),
        fetchGlobal('featuredProducts', setFeaturedProductIds, []),
        fetchGlobal('siteContent', setSiteContent, getInitialSiteContent()),
        fetchGlobal('maintenanceMode', setMaintenanceMode, false),
        fetchGlobal('paymentGatewayEnabled', setPaymentGatewayEnabled, true),
        fetchGlobal('codEnabled', setCodEnabled, true)
      ]);

      setLoading(false);
    };
    init();
  }, []);

  const saveGlobal = async (key, value) => {
    try {
      await fetch(`${API_BASE}/global/${key}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      });
    } catch (err) {
      console.error(`Error saving ${key}:`, err);
    }
  };

  const saveCoupons = async (updated) => {
    setCoupons(updated);
    await saveGlobal('coupons', updated);
  };

  const saveSettings = async (updated) => {
    setSettings(updated);
    await saveGlobal('settings', updated);
  };

  const saveCategories = async (updated) => {
    setCategories(updated);
    await saveGlobal('categories', updated);
  };

  const saveHomepage = async (updated) => {
    setHomepage(updated);
    await saveGlobal('homepage', updated);
  };

  const saveSiteContent = async (updated) => {
    setSiteContent(updated);
    await saveGlobal('siteContent', updated);
  };

  const updateHomepage = async (data) => {
    setHomepage(prev => {
      const updated = { ...prev, ...data };
      saveGlobal('homepage', updated);
      return updated;
    });
  };

  const addProduct = async (data) => {
    try {
      const res = await fetch(`${API_BASE}/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const np = await res.json();
      if (!res.ok) throw new Error(np.error || 'Failed to add product');
      setProducts(prev => [np, ...prev]);
      return np;
    } catch (err) {
      console.error("Add Product Error:", err);
      throw err;
    }
  };

  const updateProduct = async (id, data) => {
    try {
      const isMockId = typeof id === 'number' || String(id).length < 24;
      if (isMockId) {
        const existingProduct = products.find(p => p.id === id) || {};
        const { id: mockId, _id, ...productData } = { ...existingProduct, ...data };
        const res = await fetch(`${API_BASE}/products`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(productData),
        });
        const created = await res.json();
        if (!res.ok) throw new Error(created.error || 'Failed to add mock product');
        setProducts(prev => prev.map(p => p.id === id ? created : p));
        return created;
      }
      const res = await fetch(`${API_BASE}/products/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const updated = await res.json();
      if (!res.ok) throw new Error(updated.error || 'Failed to update product');
      setProducts(prev => prev.map(p => p.id === id ? updated : p));
      return updated;
    } catch (err) {
      console.error("Update Product Error:", err);
      throw err;
    }
  };

  const deleteProduct = async (id) => {
    try {
      await fetch(`${API_BASE}/products/${id}`, { method: 'DELETE' });
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error("Delete Product Error:", err);
      throw err;
    }
  };

  const toggleStatus = async (id) => {
    const p = products.find(x => x.id === id);
    if (p) await updateProduct(id, { status: p.status === 'published' ? 'draft' : 'published' });
  };

  const toggleStock = async (id) => {
    const p = products.find(x => x.id === id);
    if (p) await updateProduct(id, { stock: p.stock === 'instock' ? 'outofstock' : 'instock' });
  };

  const addCoupon = async (data) => {
    const nc = { ...data, id: Date.now() };
    await saveCoupons([...coupons, nc]);
  };
  const deleteCoupon = async (id) => await saveCoupons(coupons.filter(c => c.id !== id));
  const toggleCoupon = async (id) => await saveCoupons(coupons.map(c => c.id === id ? { ...c, active: !c.active } : c));
  const updateLogo = async (logoData) => await saveSettings({ ...settings, logo: logoData });
  const updateCategory = async (id, data) => await saveCategories(categories.map(c => c.id === id ? { ...c, ...data } : c));
  const reorderCategories = async (newOrder) => await saveCategories(newOrder);
  const resetHomepage = async () => await saveHomepage(getInitialHomepage());
  const resetCategories = async () => await saveCategories(defaultCategories);
  const saveFeaturedProducts = async (ids) => {
    setFeaturedProductIds(ids);
    await saveGlobal('featuredProducts', ids);
  };
  const saveMaintenanceMode = async (enabled) => {
    setMaintenanceMode(enabled);
    await saveGlobal('maintenanceMode', enabled);
  };
  const savePaymentGatewayStatus = async (enabled) => {
    setPaymentGatewayEnabled(enabled);
    await saveGlobal('paymentGatewayEnabled', enabled);
  };
  const saveCodStatus = async (enabled) => {
    setCodEnabled(enabled);
    await saveGlobal('codEnabled', enabled);
  };
  const addBlog = async (data) => {
    try {
      const res = await fetch(`${API_BASE}/blogs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      if (!res.ok) {
        const text = await res.text();
        console.error("Server Response:", text);
        throw new Error(`Server Error (${res.status}): ${text.slice(0, 100)}...`);
      }
      
      const nb = await res.json();
      setBlogs(prev => [nb, ...prev]);
      return nb;
    } catch (err) {
      console.error("Add Blog Error:", err);
      throw err;
    }
  };

  const updateBlog = async (id, data) => {
    try {
      const res = await fetch(`${API_BASE}/blogs/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      if (!res.ok) {
        const text = await res.text();
        console.error("Server Response:", text);
        throw new Error(`Server Error (${res.status}): ${text.slice(0, 100)}...`);
      }
      
      const updated = await res.json();
      setBlogs(prev => prev.map(b => (b._id === id || b.id === id) ? updated : b));
      return updated;
    } catch (err) {
      console.error("Update Blog Error:", err);
      throw err;
    }
  };

  const deleteBlog = async (id) => {
    try {
      await fetch(`${API_BASE}/blogs/${id}`, { method: 'DELETE' });
      setBlogs(prev => prev.filter(b => (b._id !== id && b.id !== id)));
    } catch (err) {
      console.error("Delete Blog Error:", err);
      throw err;
    }
  };

  const clearAllData = async () => {
    if (window.confirm("Clear all DB data?")) {
      await Promise.all([
        saveGlobal('coupons', getInitialCoupons()),
        saveGlobal('categories', defaultCategories),
        saveGlobal('homepage', getInitialHomepage()),
        saveGlobal('settings', { logo: null })
      ]);
      window.location.reload();
    }
  };

  const stats = {
    total: products.length,
    published: products.filter(p => p.status === 'published').length,
    draft: products.filter(p => p.status === 'draft').length,
    outofstock: products.filter(p => p.stock === 'outofstock').length,
    onSale: products.filter(p => p.price < p.mrp).length,
    categories: categories.length,
  };

  return (
    <ProductStoreContext.Provider value={{
      products, blogs, coupons, stats, categories, subcategories, settings, homepage, loading,
      featuredProductIds, siteContent,
      getFullUrl, addProduct, updateProduct, deleteProduct,
      addBlog, updateBlog, deleteBlog,
      toggleStatus, toggleStock, addCoupon, deleteCoupon, toggleCoupon,
      updateLogo, updateCategory, reorderCategories, updateHomepage,
      saveSiteContent, saveHomepage, resetHomepage, resetCategories, clearAllData,
      saveFeaturedProducts, maintenanceMode, saveMaintenanceMode,
      paymentGatewayEnabled, savePaymentGatewayStatus,
      codEnabled, saveCodStatus
    }}>
      {children}
    </ProductStoreContext.Provider>
  );
}

export const useStore = () => useContext(ProductStoreContext);
